import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  FlatList,
  Image,
  SectionList,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { SafeAreaView } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useQuery } from "@tanstack/react-query";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import {
  ChevronLeft,
  Compass,
  Heart,
  Library,
  Music2,
  Play,
  Search,
  X,
} from "lucide-react-native";
import { RootStackParamList } from "../navigation/types";
import {
  getItems,
  getMusicGenres,
  getMusicCollection,
  imageSource,
  JellyfinItem,
} from "../api/jellyfin";
import { musicPlaylistsOnly } from "../utils/musicPlaylists";
import { useSession } from "../session/SessionProvider";
import { FocusablePressable } from "../components/FocusablePressable";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { getMusicHomeHeaderAccent } from "../utils/imageAccent";
import { colors, systemSansFont } from "../theme/tokens";
import { useMusicPlayback } from "../music/MusicPlaybackProvider";
import { useMusicScrollIndicator } from "../components/MusicScrollIndicator";
import { useSideRail } from "../components/AppShellContext";

type Props = NativeStackScreenProps<RootStackParamList, "Music">;
type MusicSection = {
  title: string;
  artist?: boolean;
  tracks?: boolean;
  data: JellyfinItem[][];
};

type LibraryFilter = "All" | "Playlists" | "Albums" | "Artists";

export function MusicLibrarySidebar({
  playlists,
  albums,
  artists = [],
  collapsed,
  onToggleCollapsed,
  onOpen,
  onDestination,
  activeDestination,
  activeItemId,
}: {
  playlists: JellyfinItem[];
  albums: JellyfinItem[];
  artists?: JellyfinItem[];
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  onOpen?: (item: JellyfinItem) => void;
  onDestination?: (view: "liked" | "browse") => void;
  activeDestination?: "liked" | "browse";
  activeItemId?: string;
}) {
  const { credentials } = useSession();
  const [filter, setFilter] = useState<LibraryFilter>("All");
  const [librarySearch, setLibrarySearch] = useState("");
  const libraryScrollbar = useMusicScrollIndicator(0, 0.15);
  const libraryItems = useMemo(() => {
    const source =
      filter === "Playlists"
        ? playlists
        : filter === "Albums"
          ? albums
          : filter === "Artists"
            ? artists
            : [...playlists, ...albums, ...artists];
    const query = librarySearch.trim().toLocaleLowerCase();
    return source.filter(
      (item) => !query || (item.Name ?? "").toLocaleLowerCase().includes(query),
    );
  }, [albums, artists, filter, librarySearch, playlists]);
  const filters: LibraryFilter[] = ["All", "Playlists", "Albums", "Artists"];
  return (
    <View style={[styles.sidebar, collapsed && styles.sidebarCollapsed]}>
      <FocusablePressable
        accessibilityLabel={collapsed ? "Expand your library" : "Collapse your library"}
        onPress={onToggleCollapsed}
        style={(focused, pressed, hovered) => [styles.sidebarTitleRow, collapsed && styles.sidebarTitleRowCollapsed, (focused || hovered) && styles.sidebarTitleRowActive, pressed && styles.pressed]}
      >
        <Library color="#a7a7a7" size={19} />
        {!collapsed ? <Text style={styles.sidebarTitle}>Your Library</Text> : null}
        <ChevronLeft color={collapsed ? "#d0d0d0" : "#a7a7a7"} size={18} style={{ transform: [{ rotate: collapsed ? "180deg" : "0deg" }] }} />
      </FocusablePressable>
      <View style={styles.sidebarDestinations}>
        <FocusablePressable
          accessibilityLabel="Liked Songs"
          onPress={() => onDestination?.("liked")}
          style={(focused, pressed, hovered) => [
            styles.sidebarDestination,
            (activeDestination === "liked" || focused || hovered) &&
              styles.sidebarDestinationActive,
            pressed && styles.pressed,
          ]}
        >
          <View style={styles.likedIcon}>
            <Heart color="#fff" fill="#fff" size={17} />
          </View>
          {!collapsed ? (
            <Text style={styles.sidebarDestinationText}>Liked Songs</Text>
          ) : null}
        </FocusablePressable>
        <FocusablePressable
          accessibilityLabel="Browse music"
          onPress={() => onDestination?.("browse")}
          style={(focused, pressed, hovered) => [
            styles.sidebarDestination,
            (activeDestination === "browse" || focused || hovered) &&
              styles.sidebarDestinationActive,
            pressed && styles.pressed,
          ]}
        >
          <View style={styles.browseIcon}>
            <Compass color="#a7a7a7" size={17} />
          </View>
          {!collapsed ? (
            <Text style={styles.sidebarDestinationText}>Browse</Text>
          ) : null}
        </FocusablePressable>
      </View>
      {!collapsed ? (
        <>
          <View style={styles.sidebarFilters}>
            {filters.map((value) => (
              <FocusablePressable
                key={value}
                accessibilityLabel={`Show ${value.toLowerCase()} in your library`}
                onPress={() => setFilter(value)}
                style={(focused, pressed, hovered) => [
                  filter === value
                    ? styles.sidebarFilterActive
                    : styles.sidebarFilter,
                  (focused || hovered) && styles.sidebarFilterHovered,
                  pressed && styles.pressed,
                ]}
              >
                <Text
                  style={
                    filter === value
                      ? styles.sidebarFilterActiveText
                      : styles.sidebarFilterText
                  }
                >
                  {value}
                </Text>
              </FocusablePressable>
            ))}
          </View>
          <View style={styles.sidebarSearch}>
            <Search color="#858585" size={15} />
            <TextInput
              value={librarySearch}
              onChangeText={setLibrarySearch}
              placeholder="Search your library"
              placeholderTextColor="#7d7d7d"
              selectionColor={colors.brand}
              style={styles.sidebarSearchInput}
            />
          </View>
        </>
      ) : null}
      <View style={styles.sidebarListWrap}>
      <FlatList
        {...libraryScrollbar.listProps}
        data={libraryItems}
        keyExtractor={(item, index) => item.Id ?? `library-${index}`}
        contentContainerStyle={styles.sidebarList}
        renderItem={({ item }) => (
          <FocusablePressable
            accessibilityLabel={`Open ${item.Name ?? "library item"}`}
            onPress={() => onOpen?.(item)}
            style={(focused, pressed, hovered) => [
              styles.sidebarItem,
              (activeItemId === item.Id || focused || hovered) &&
                styles.sidebarItemActive,
              pressed && styles.pressed,
            ]}
          >
            <View style={styles.sidebarThumb}>
              {credentials && item.Id && item.ImageTags?.Primary ? (
                <Image
                  source={imageSource(credentials, item, "Primary", 88)}
                  resizeMode="cover"
                  style={styles.sidebarThumbImage}
                />
              ) : (
                <Music2 color="#999" size={18} />
              )}
            </View>
            {!collapsed ? (
              <View style={styles.sidebarItemCopy}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.sidebarItemTitle,
                    activeItemId === item.Id && styles.sidebarItemTitleSelected,
                  ]}
                >
                  {item.Name ?? "Untitled"}
                </Text>
                <Text numberOfLines={1} style={styles.sidebarItemType}>
                  {item.Type === "Playlist"
                    ? "Playlist"
                    : item.Type === "MusicArtist"
                      ? "Artist"
                      : "Album"}
                </Text>
              </View>
            ) : null}
          </FocusablePressable>
        )}
      />
      {libraryScrollbar.indicator}
      </View>
    </View>
  );
}

function greeting(name?: string) {
  const hour = new Date().getHours();
  const prefix =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return name ? `${prefix}, ${name}` : prefix;
}

function useHoverReveal() {
  const value = useRef(new Animated.Value(0)).current;
  const setVisible = useCallback(
    (visible: boolean) => {
      value.stopAnimation();
      Animated.timing(value, {
        toValue: visible ? 1 : 0,
        duration: visible ? 180 : 140,
        easing: visible ? Easing.out(Easing.cubic) : Easing.inOut(Easing.quad),
        useNativeDriver: true,
      }).start();
    },
    [value],
  );
  return { value, setVisible };
}

function QuickPick({
  item,
  credentials,
  compact,
  width,
  onPress,
  onPlay,
}: {
  item: JellyfinItem;
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  compact: boolean;
  width: number;
  onPress: () => void;
  onPlay: () => void;
}) {
  const reveal = useHoverReveal();
  return (
    <FocusablePressable
      accessibilityLabel={`Open ${item.Name ?? "album"}`}
      onPress={onPress}
      onHoverIn={() => reveal.setVisible(true)}
      onHoverOut={() => reveal.setVisible(false)}
      onFocus={() => reveal.setVisible(true)}
      onBlur={() => reveal.setVisible(false)}
      style={(focused, pressed, hovered) => [
        styles.quickPick,
        { width },
        compact && styles.quickPickCompact,
        (focused || hovered) && styles.quickPickActive,
        pressed && styles.pressed,
      ]}
    >
      <Image
        source={imageSource(credentials, item, "Primary", 128)}
        resizeMode="cover"
        style={[styles.quickArt, compact && styles.quickArtCompact]}
      />
      <Text numberOfLines={1} style={styles.quickTitle}>
        {item.Name ?? "Untitled"}
      </Text>
      <Animated.View
        style={[
          styles.quickPlay,
          {
            opacity: reveal.value,
            transform: [
              {
                scale: reveal.value.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.92, 1],
                }),
              },
            ],
          },
        ]}
      >
        <FocusablePressable
          accessibilityLabel={`Play ${item.Name ?? "album"}`}
          onHoverIn={() => reveal.setVisible(true)}
          onFocus={() => reveal.setVisible(true)}
          onPress={(event) => {
            event.stopPropagation();
            onPlay();
          }}
          style={(focused, pressed, hovered) => [styles.quickPlayButton, (focused || hovered) && styles.playControlActive, pressed && styles.pressed]}
        >
          <Play color="#fff" fill="#fff" size={15} />
        </FocusablePressable>
      </Animated.View>
    </FocusablePressable>
  );
}

function MusicTile({
  item,
  artist,
  width,
  onPress,
  onPlay,
}: {
  item: JellyfinItem;
  artist?: boolean;
  width: number;
  onPress: () => void;
  onPlay?: () => void;
}) {
  const { credentials } = useSession();
  const [failed, setFailed] = useState(false);
  const reveal = useHoverReveal();
  if (!credentials) return null;
  return (
    <FocusablePressable
      accessibilityLabel={`Open ${item.Name ?? "music"}`}
      onPress={onPress}
      onHoverIn={() => reveal.setVisible(true)}
      onHoverOut={() => reveal.setVisible(false)}
      onFocus={() => reveal.setVisible(true)}
      onBlur={() => reveal.setVisible(false)}
      style={(focused, pressed, hovered) => [
        styles.tile,
        { width },
        (focused || hovered) && styles.tileActive,
        pressed && styles.pressed,
      ]}
    >
      {() => (
        <>
          <View
            style={[
              styles.tileArt,
              artist && styles.tileArtArtist,
              { width, height: width },
            ]}
          >
            {!failed ? (
              <Image
                source={imageSource(credentials, item, "Primary", width * 2)}
                onError={() => setFailed(true)}
                resizeMode="cover"
                style={[
                  styles.tileImage,
                  artist && styles.tileArtArtist,
                  { width, height: width },
                ]}
              />
            ) : (
              <Music2 color="#999" size={Math.round(width * 0.34)} />
            )}
            {!artist && onPlay ? (
              <Animated.View
                style={[
                  styles.tilePlay,
                  {
                    opacity: reveal.value,
                    transform: [
                      {
                        translateY: reveal.value.interpolate({
                          inputRange: [0, 1],
                          outputRange: [10, 0],
                        }),
                      },
                    ],
                  },
                ]}
              >
                <FocusablePressable
                  accessibilityLabel={`Play ${item.Name ?? "album"}`}
                  onHoverIn={() => reveal.setVisible(true)}
                  onFocus={() => reveal.setVisible(true)}
                  onPress={(event) => {
                    event.stopPropagation();
                    onPlay();
                  }}
                  style={(focused, pressed, hovered) => [styles.tilePlayButton, (focused || hovered) && styles.playControlActive, pressed && styles.pressed]}
                >
                  <Play color="#fff" fill="#fff" size={20} />
                </FocusablePressable>
              </Animated.View>
            ) : null}
          </View>
          <Text numberOfLines={1} style={styles.tileTitle}>
            {item.Name ?? "Untitled"}
          </Text>
          <Text numberOfLines={1} style={styles.tileSubtitle}>
            {artist
              ? "Artist"
              : item.Type === "Playlist"
                ? "Playlist"
                : (item.AlbumArtist ??
                  item.ArtistItems?.[0]?.Name ??
                  item.ProductionYear ??
                  "Album")}
          </Text>
        </>
      )}
    </FocusablePressable>
  );
}

function MusicSearchTracks({
  items,
  credentials,
  onOpen,
}: {
  items: JellyfinItem[];
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  onOpen: (item: JellyfinItem) => void;
}) {
  return (
    <FlatList
      scrollEnabled={false}
      data={items.slice(0, 6)}
      keyExtractor={(item, index) => item.Id ?? `music-search-track-${index}`}
      contentContainerStyle={styles.searchTracks}
      renderItem={({ item }) => (
        <FocusablePressable
          accessibilityLabel={`Open ${item.Name ?? "song"}`}
          onPress={() => onOpen(item)}
          style={(focused, pressed, hovered) => [
            styles.searchTrack,
            (focused || hovered) && styles.searchTrackActive,
            pressed && styles.pressed,
          ]}
        >
          <Image
            source={imageSource(credentials, item, "Primary", 80)}
            resizeMode="cover"
            style={styles.searchTrackArt}
          />
          <View style={styles.searchTrackCopy}>
            <Text numberOfLines={1} style={styles.searchTrackTitle}>
              {item.Name ?? "Untitled"}
            </Text>
            <Text numberOfLines={1} style={styles.searchTrackArtist}>
              {item.ArtistItems?.map((artist) => artist.Name)
                .filter(Boolean)
                .join(", ") ||
                item.AlbumArtist ||
                "Unknown artist"}
            </Text>
          </View>
        </FocusablePressable>
      )}
    />
  );
}

export function MusicHomeScreen({ navigation }: Props) {
  const sideRail = useSideRail();
  const { width } = useReferenceWindowDimensions();
  const compact = width <= 700;
  const phone = width < 600;
  const [sidebarOverride, setSidebarOverride] = useState<boolean>();
  const sidebarCollapsed = sidebarOverride ?? width <= 950;
  const { credentials, user } = useSession();
  const musicPlayback = useMusicPlayback();
  const musicScrollbar = useMusicScrollIndicator();
  const bottomNavInset = useBottomNavInset();
  const playCollection = useCallback(
    async (item: JellyfinItem) => {
      if (!credentials) return;
      const tracks = item.Type === "Audio"
        ? [item]
        : await getMusicCollection(credentials, item);
      if (tracks.length) musicPlayback.loadQueue(tracks, 0, true);
    },
    [credentials, musicPlayback.loadQueue],
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [headerAccent, setHeaderAccent] = useState("#1e1e1e");
  const headerAccentReveal = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const common = { enabled: Boolean(credentials), staleTime: 60_000 };
  const recentTracks = useQuery({
    queryKey: ["music-home", "recent-tracks"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "Audio",
        SortBy: "DatePlayed",
        SortOrder: "Descending",
        Limit: 30,
        Fields:
          "Album,AlbumId,AlbumArtist,AlbumPrimaryImageTag,ArtistItems,UserData",
        EnableUserData: true,
      }),
    ...common,
  });
  const recentAlbums = useQuery({
    queryKey: ["music-home", "recent-albums"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicAlbum",
        SortBy: "DateCreated",
        SortOrder: "Descending",
        Limit: 40,
        Fields: "AlbumArtist,ArtistItems,UserData",
      }),
    ...common,
  });
  const playlists = useQuery({
    queryKey: ["music-home", "playlists"],
    queryFn: async () =>
      musicPlaylistsOnly(await getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "Playlist",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Fields: "MediaType,UserData",
      })),
    ...common,
  });
  const favoriteAlbums = useQuery({
    queryKey: ["music-home", "favorites"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicAlbum",
        Filters: "IsFavorite",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Limit: 20,
        Fields: "AlbumArtist,ArtistItems,UserData",
      }),
    ...common,
  });
  const mostPlayed = useQuery({
    queryKey: ["music-home", "most-played"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicAlbum",
        SortBy: "PlayCount",
        SortOrder: "Descending",
        Limit: 20,
        Fields: "AlbumArtist,ArtistItems,UserData",
      }),
    ...common,
  });
  const topArtists = useQuery({
    queryKey: ["music-home", "artists"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicArtist",
        SortBy: "PlayCount",
        SortOrder: "Descending",
        Limit: 40,
        Fields: "UserData",
      }),
    ...common,
  });
  const genres = useQuery({
    queryKey: ["music-home", "genres"],
    queryFn: () => getMusicGenres(credentials!, 30),
    ...common,
  });
  const searchEnabled = Boolean(credentials && debouncedSearch.length > 1);
  const searchSongs = useQuery({
    queryKey: ["music-home", "search", "songs", debouncedSearch],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        SearchTerm: debouncedSearch,
        IncludeItemTypes: "Audio",
        Limit: 6,
        Fields: "Album,AlbumId,AlbumArtist,ArtistItems,UserData",
      }),
    enabled: searchEnabled,
  });
  const searchAlbums = useQuery({
    queryKey: ["music-home", "search", "albums", debouncedSearch],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        SearchTerm: debouncedSearch,
        IncludeItemTypes: "MusicAlbum",
        Limit: 6,
        Fields: "AlbumArtist,ArtistItems,UserData",
      }),
    enabled: searchEnabled,
  });
  const searchArtists = useQuery({
    queryKey: ["music-home", "search", "artists", debouncedSearch],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        SearchTerm: debouncedSearch,
        IncludeItemTypes: "MusicArtist",
        Limit: 6,
        Fields: "UserData",
      }),
    enabled: searchEnabled,
  });
  const quickPicks = useMemo(() => {
    const seen = new Set<string>();
    return (recentTracks.data ?? [])
      .flatMap((track) => {
        if (!track.AlbumId || seen.has(track.AlbumId)) return [];
        seen.add(track.AlbumId);
        return [
          {
            ...track,
            Id: track.AlbumId,
            Name: track.Album ?? track.Name,
            Type: "MusicAlbum",
            ImageTags: track.AlbumPrimaryImageTag
              ? { Primary: track.AlbumPrimaryImageTag }
              : track.ImageTags,
          },
        ];
      })
      .slice(0, 6);
  }, [recentTracks.data]);
  useEffect(() => {
    let active = true;
    const first = quickPicks[0];
    if (credentials && first) {
      headerAccentReveal.stopAnimation();
      headerAccentReveal.setValue(0);
      void getMusicHomeHeaderAccent(credentials, first)
        .then((value) => {
          if (!active) return;
          setHeaderAccent(value);
          Animated.timing(headerAccentReveal, {
            toValue: 1,
            duration: 260,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }).start();
        })
        .catch(() => undefined);
    }
    return () => {
      active = false;
    };
  }, [credentials, headerAccentReveal, quickPicks]);
  if (!credentials) return null;
  const searching = search.trim().length > 1;
  const sections: MusicSection[] = searching
    ? [
        { title: "Songs", tracks: true, data: [searchSongs.data ?? []] },
        { title: "Albums", data: [searchAlbums.data ?? []] },
        { title: "Artists", artist: true, data: [searchArtists.data ?? []] },
      ].filter((section) => section.data[0].length)
    : [
        { title: "Your Playlists", data: [playlists.data ?? []] },
        { title: "Recently Added", data: [recentAlbums.data ?? []] },
        { title: "Liked Albums", data: [favoriteAlbums.data ?? []] },
        { title: "Recently Played", data: [mostPlayed.data ?? []] },
        {
          title: "Your Top Artists",
          artist: true,
          data: [topArtists.data ?? []],
        },
      ].filter((section) => section.data[0].length);
  const sidebarWidth = phone ? 0 : sidebarCollapsed ? 72 : 320;
  const mainWidth = width - (phone ? 8 : 24 + sidebarWidth);
  const contentWidth = mainWidth - (compact ? 32 : 48);
  const tileGap = compact ? 12 : 16;
  const tileMinimum = compact ? 150 : width >= 1400 ? 180 : 160;
  const tileColumns = Math.max(
    1,
    Math.floor((contentWidth + tileGap) / (tileMinimum + tileGap)),
  );
  const tileWidth = Math.floor(
    (contentWidth - tileGap * (tileColumns - 1)) / tileColumns,
  );
  const quickColumns = compact ? 2 : 3;
  const quickRows = Math.max(1, Math.ceil(quickPicks.length / quickColumns));
  const musicTopHeight = compact ? 70 + quickRows * 56 : 90 + quickRows * 72;
  const quickWidth = Math.floor(
    (contentWidth - 8 * (quickColumns - 1)) / quickColumns,
  );
  return (
    <SafeAreaView style={styles.safe} edges={[]}>
              <View style={[styles.shell, compact && styles.shellCompact, sideRail && !compact && styles.shellWithRail]}>
        {!phone ? (
          <MusicLibrarySidebar
            playlists={playlists.data ?? []}
            albums={recentAlbums.data ?? []}
            artists={topArtists.data ?? []}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarOverride((value) => !(value ?? width <= 950))}
            onOpen={(entry) =>
              entry.Id && navigation.navigate("Item", { itemId: entry.Id })
            }
            onDestination={(view) => navigation.navigate("Music", { view })}
          />
        ) : null}
        <View style={styles.mainPanel}>
        <SectionList<JellyfinItem[], MusicSection>
          {...musicScrollbar.listProps}
          style={styles.mainList}
          sections={sections}
          keyExtractor={(_, index) => `music-section-${index}`}
          stickySectionHeadersEnabled={false}
          contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
          ListHeaderComponent={
            <>
              <View
                style={[styles.searchWrap, compact && styles.searchWrapCompact]}
              >
                <View
                  style={[
                    styles.searchBox,
                    searchFocused && styles.searchBoxFocused,
                  ]}
                >
                  <Search color="rgba(255,255,255,0.5)" size={16} />
                  <TextInput
                    value={search}
                    onChangeText={setSearch}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    placeholder="What do you want to listen to?"
                    placeholderTextColor="#737373"
                    selectionColor={colors.brand}
                    style={styles.searchInput}
                  />
                  {search ? (
                    <FocusablePressable
                      accessibilityLabel="Clear music search"
                      onPress={() => setSearch("")}
                      style={(focused, pressed, hovered) => [
                        styles.clear,
                        (focused || hovered) && styles.clearActive,
                        pressed && styles.pressed,
                      ]}
                    >
                      <X color="rgba(255,255,255,0.65)" size={16} />
                    </FocusablePressable>
                  ) : null}
                </View>
              </View>
              {!searching ? (
                <View
                  style={[
                    styles.top,
                    compact && styles.topCompact,
                  ]}
                >
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      StyleSheet.absoluteFillObject,
                      {
                        backgroundColor: headerAccent,
                        opacity: headerAccentReveal,
                      },
                    ]}
                  />
                  <Svg
                    pointerEvents="none"
                    width={width}
                    height={musicTopHeight}
                    style={styles.topGradient}
                  >
                    <Defs>
                      <LinearGradient
                        id="music-home-fade"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <Stop offset="0" stopColor="#121212" stopOpacity="0" />
                        <Stop offset="1" stopColor="#121212" />
                      </LinearGradient>
                    </Defs>
                    <Rect
                      width={width}
                      height={musicTopHeight}
                      fill="url(#music-home-fade)"
                    />
                  </Svg>
                  <Text
                    style={[styles.greeting, compact && styles.greetingCompact]}
                  >
                    {greeting(user?.Name)}
                  </Text>
                  <FlatList
                    scrollEnabled={false}
                    key={`quick-${quickColumns}`}
                    data={quickPicks}
                    numColumns={quickColumns}
                    keyExtractor={(item, index) => item.Id ?? `quick-${index}`}
                    columnWrapperStyle={styles.quickRow}
                    renderItem={({ item }) => (
                      <QuickPick
                        item={item}
                        credentials={credentials}
                        compact={compact}
                        width={quickWidth}
                        onPress={() =>
                          item.Id &&
                          navigation.navigate("Item", { itemId: item.Id })
                        }
                        onPlay={() => void playCollection(item)}
                      />
                    )}
                  />
                </View>
              ) : null}
              {!searching && genres.data?.length ? (
                <View
                  style={[
                    styles.genreSection,
                    compact && styles.sectionCompact,
                  ]}
                >
                  <Text
                    style={[
                      styles.sectionTitle,
                      compact && styles.sectionTitleCompact,
                    ]}
                  >
                    Browse by genre
                  </Text>
                  <View style={styles.genreRow}>
                    {genres.data.map((item, index) => (
                      <FocusablePressable
                        key={item.Id ?? `genre-${index}`}
                        onPress={() =>
                          item.Id &&
                          navigation.navigate("Genre", {
                            genreId: item.Id,
                            name: item.Name ?? "Genre",
                          })
                        }
                        style={(focused, pressed, hovered) => [
                          styles.genreChip,
                          (focused || hovered) && styles.genreChipActive,
                          pressed && styles.pressed,
                        ]}
                      >
                        <Text style={styles.genreText}>
                          {item.Name ?? "Genre"}
                        </Text>
                      </FocusablePressable>
                    ))}
                  </View>
                </View>
              ) : null}
            </>
          }
          renderSectionHeader={({ section }) => (
            <Text
              style={[
                styles.sectionTitle,
                styles.rowTitle,
                compact && styles.rowTitleCompact,
              ]}
            >
              {section.title}
            </Text>
          )}
          renderItem={({ item: row, section }) =>
            section.tracks ? (
              <MusicSearchTracks
                items={row}
                credentials={credentials}
                onOpen={(item) =>
                  (item.AlbumId || item.Id) &&
                  navigation.navigate("Item", {
                    itemId: item.AlbumId || item.Id!,
                  })
                }
              />
            ) : (
              <FlatList
                horizontal
                data={row}
                keyExtractor={(entry, index) => entry.Id ?? `music-${index}`}
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={[
                  styles.tileRow,
                  compact && styles.tileRowCompact,
                ]}
                renderItem={({ item }) => (
                  <MusicTile
                    item={item}
                    artist={section.artist}
                    width={tileWidth}
                    onPress={() =>
                      item.Id &&
                      navigation.navigate("Item", { itemId: item.Id })
                    }
                    onPlay={section.artist ? undefined : () => void playCollection(item)}
                  />
                )}
              />
            )
          }
          ListEmptyComponent={
            searchSongs.isLoading ||
            searchAlbums.isLoading ||
            searchArtists.isLoading ? (
              <ActivityIndicator color={colors.brand} style={styles.loader} />
            ) : searching ? (
              <Text style={styles.empty}>
                No results for “{search.trim()}”.
              </Text>
            ) : null
          }
        />
        {musicScrollbar.indicator}
        </View>
      </View>
      <MatureTopNav navigation={navigation} active="Music" overlay />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#000" },
  shell: {
    flex: 1,
    paddingTop: 80,
    paddingHorizontal: 8,
    paddingBottom: 8,
    flexDirection: "row",
    gap: 8,
  },
  shellCompact: { paddingTop: 124, paddingHorizontal: 4, paddingBottom: 0 },
  shellWithRail: { paddingLeft: 64 },
  mainPanel: { flex: 1, borderRadius: 8, backgroundColor: "#121212" },
  mainList: { flex: 1 },
  content: { paddingBottom: 80 },
  searchWrap: { paddingHorizontal: 24, paddingTop: 16, paddingBottom: 8 },
  searchWrapCompact: { paddingHorizontal: 16, paddingTop: 12 },
  searchBox: {
    maxWidth: 400,
    height: 40,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "transparent",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  searchBoxFocused: {
    borderColor: "rgba(255,255,255,0.2)",
    backgroundColor: "rgba(255,255,255,0.14)",
  },
  searchInput: {
    flex: 1,
    paddingVertical: 0,
    color: colors.text,
    fontSize: 14,
  },
  clear: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  clearActive: { backgroundColor: "rgba(255,255,255,0.1)" },
  top: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 20,
    overflow: "hidden",
  },
  topCompact: { paddingHorizontal: 16, paddingTop: 12, paddingBottom: 12 },
  topGradient: { position: "absolute", left: 0, bottom: 0 },
  greeting: {
    marginBottom: 20,
    color: colors.text,
    fontSize: 28,
    lineHeight: 34,
    fontWeight: "800",
  },
  greetingCompact: { fontSize: 21, lineHeight: 26 },
  quickRow: { gap: 8, marginBottom: 8 },
  quickPick: {
    height: 64,
    flexGrow: 0,
    flexShrink: 0,
    borderRadius: 4,
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  quickPickCompact: { height: 48 },
  quickPickActive: { backgroundColor: "rgba(255,255,255,0.22)" },
  quickArt: { width: 64, height: 64 },
  quickArtCompact: { width: 48, height: 48 },
  quickTitle: {
    flex: 1,
    paddingHorizontal: 12,
    color: colors.text,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "800",
  },
  quickPlay: {
    width: 34,
    height: 34,
    marginRight: 10,
    borderRadius: 17,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  quickPlayButton: { width: 34, height: 34, borderRadius: 17, alignItems: "center", justifyContent: "center", borderWidth: 2, borderColor: "transparent" },
  quickPlayHidden: { opacity: 0 },
  genreSection: { paddingHorizontal: 24, paddingTop: 12, marginBottom: 32 },
  sectionCompact: { paddingHorizontal: 16, paddingTop: 6 },
  sectionTitle: {
    color: colors.text,
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "800",
  },
  sectionTitleCompact: { fontSize: 19, lineHeight: 24 },
  genreRow: { paddingTop: 4, flexDirection: "row", flexWrap: "wrap", gap: 8 },
  genreChip: {
    minHeight: 32,
    paddingHorizontal: 14,
    borderRadius: 20,
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  genreChipActive: { backgroundColor: "rgba(255,255,255,0.2)" },
  genreText: {
    color: colors.text,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },
  rowTitle: { marginHorizontal: 24, marginBottom: 12 },
  rowTitleCompact: { marginHorizontal: 16, fontSize: 19, lineHeight: 24 },
  tileRow: { paddingHorizontal: 24, paddingBottom: 32, gap: 16 },
  tileRowCompact: { paddingHorizontal: 16, gap: 12 },
  tile: { borderRadius: 6 },
  tileActive: { transform: [{ scale: 1.015 }] },
  tileArt: {
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    borderRadius: 6,
    backgroundColor: "#282828",
  },
  tileArtArtist: { borderRadius: 999 },
  tileImage: { borderRadius: 6 },
  tilePlay: {
    position: "absolute",
    right: 10,
    bottom: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  tilePlayButton: { width: 40, height: 40, borderRadius: 20, alignItems: "center", justifyContent: "center", borderWidth: 2, borderColor: "transparent" },
  playControlActive: { borderColor: colors.text, transform: [{ scale: 1.05 }] },
  tilePlayHidden: { opacity: 0, transform: [{ translateY: 10 }] },
  tileFallback: {
    color: "rgba(255,255,255,0.36)",
    fontSize: 32,
    fontWeight: "900",
  },
  tileTitle: {
    marginTop: 20,
    marginHorizontal: 4,
    color: colors.text,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "700",
  },
  tileSubtitle: {
    marginTop: 4,
    marginHorizontal: 4,
    color: "#b3b3b3",
    fontSize: 13,
    lineHeight: 17,
  },
  searchTracks: { paddingHorizontal: 24, paddingBottom: 8, gap: 4 },
  searchTrack: {
    minHeight: 52,
    paddingHorizontal: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderRadius: 4,
  },
  searchTrackActive: { backgroundColor: "rgba(255,255,255,0.1)" },
  searchTrackArt: {
    width: 40,
    height: 40,
    borderRadius: 2,
    backgroundColor: "#282828",
  },
  searchTrackCopy: { flex: 1, minWidth: 0 },
  searchTrackTitle: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "500",
  },
  searchTrackArtist: {
    marginTop: 2,
    color: "#b3b3b3",
    fontSize: 12,
    lineHeight: 16,
  },
  sidebar: {
    width: 320,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#121212",
  },
  sidebarCollapsed: { width: 72 },
  sidebarTitleRow: {
    height: 48,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  sidebarTitleRowActive: { backgroundColor: "rgba(255,255,255,0.06)" },
  sidebarTitleRowCollapsed: { paddingHorizontal: 9, justifyContent: "space-between", gap: 0 },
  sidebarTitle: {
    flex: 1,
    color: "#b3b3b3",
    fontSize: 14,
    lineHeight: 20,
    fontFamily: systemSansFont,
    fontWeight: "700",
  },
  sidebarDestinations: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    gap: 4,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.07)",
  },
  sidebarDestination: {
    height: 52,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderRadius: 5,
  },
  sidebarDestinationActive: { backgroundColor: "rgba(255,255,255,0.07)" },
  likedIcon: {
    width: 44,
    height: 44,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#7967ed",
  },
  browseIcon: {
    width: 44,
    height: 44,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#292929",
  },
  sidebarDestinationText: { color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 14, fontWeight: "500" },
  sidebarFilters: {
    height: 40,
    paddingHorizontal: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  sidebarFilter: {
    height: 26,
    paddingHorizontal: 10,
    borderRadius: 15,
    justifyContent: "center",
    backgroundColor: "#2a2a2a",
  },
  sidebarFilterActive: {
    height: 26,
    paddingHorizontal: 10,
    borderRadius: 15,
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  sidebarFilterHovered: { transform: [{ scale: 1.03 }] },
  sidebarFilterText: { color: "#fff", fontFamily: systemSansFont, fontSize: 12, fontWeight: "600" },
  sidebarFilterActiveText: { color: "#111", fontFamily: systemSansFont, fontSize: 12, fontWeight: "600" },
  sidebarSearch: {
    height: 34,
    marginHorizontal: 4,
    paddingHorizontal: 10,
    borderRadius: 5,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#242424",
  },
  sidebarSearchInput: {
    flex: 1,
    height: 32,
    paddingVertical: 0,
    color: colors.text,
    fontSize: 12,
    fontFamily: systemSansFont,
  },
  sidebarList: { paddingTop: 0, paddingHorizontal: 4, paddingBottom: 16 },
  sidebarListWrap: { flex: 1 },
  sidebarItem: {
    height: 56,
    paddingHorizontal: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderRadius: 4,
  },
  sidebarItemActive: { backgroundColor: "rgba(255,255,255,0.08)" },
  sidebarThumb: {
    width: 44,
    height: 44,
    borderRadius: 3,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#292929",
  },
  sidebarThumbImage: { width: 44, height: 44 },
  sidebarItemCopy: { flex: 1, minWidth: 0 },
  sidebarItemTitle: { color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 14, lineHeight: 19, fontWeight: "500" },
  sidebarItemTitleSelected: { color: "#fff" },
  sidebarItemType: { marginTop: 2, color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 12, lineHeight: 17 },
  loader: { marginTop: 60 },
  empty: {
    marginHorizontal: 24,
    marginTop: 40,
    color: "#b3b3b3",
    fontSize: 14,
  },
  pressed: { opacity: 0.76 },
});
